import { useMemo, useSyncExternalStore } from 'react';
import { Upload as TusUpload, type DetailedError } from 'tus-js-client';
import type {
  StepVideoUpload,
  StepVideoUploadHandlers,
  StepVideoUploadStartOptions,
  StepVideoUploadStatus,
  StepVideoUploadSubject,
} from './types';

// Канал один, но маленький файл не должен ждать за большим
const MAX_PARALLEL_UPLOADS = 2;
// 50 MB: кратно 256 KiB, как требует Stream, и меньше его предела в 200 MB на чанк
const CHUNK_SIZE = 50 * 1024 * 1024;
const RETRY_DELAYS = [0, 3000, 5000, 10000, 20000, 60000];
// Байты не уходят дольше этого срока — соединение зависло; загрузка продолжается с места обрыва
const STALL_TIMEOUT_MS = 5 * 60 * 1000;
const STALL_RESUME_LIMIT = 3;
// Принятая загрузка видна ещё немного; дальше состояние шага приходит с сервера
const UPLOADED_KEEP_MS = 20000;
const UPLOADED_REFRESH_MS = 17000;

export const ACTIVE_UPLOAD_STATUSES: StepVideoUploadStatus[] = [
  'queued',
  'preparing',
  'uploading',
  'paused',
];

export const isActiveUpload = (upload: StepVideoUpload) =>
  ACTIVE_UPLOAD_STATUSES.includes(upload.status);

interface UploadEntry {
  state: StepVideoUpload;
  file: File;
  handlers: StepVideoUploadHandlers;
  upload: TusUpload | null;
  // Адрес выдан: у шага есть заготовка, при отмене её нужно убрать
  hasUploadUrl: boolean;
  // Адрес незавершённой загрузки того же файла; расходуется при запуске, дальше tus держит его сам
  resumeUrl: string | null;
  // Докачка началась, но байты ещё не пошли: мёртвый адрес заменяется новым токеном без ошибки
  resuming: boolean;
  stallTimer: ReturnType<typeof setTimeout> | null;
  stallResumes: number;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  const { data, message } = (error ?? {}) as {
    data?: { message?: string; error?: { message?: string } };
    message?: string;
  };
  return data?.message || data?.error?.message || message || fallback;
};

const getResponseStatus = (error: Error | DetailedError) =>
  'originalResponse' in error && error.originalResponse ? error.originalResponse.getStatus() : 0;

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

const preventUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  // Часть браузеров показывает предупреждение только при заданном returnValue
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
};

/**
 * Загрузки видео шагов живут здесь, а не в карточке или модалке: окно можно закрыть, загрузка
 * продолжится, а карточка при повторном открытии подхватит её состояние по stepId.
 * Загрузку в Stream создаёт бэкенд (upload-video-token), клиент докачивает байты по выданному
 * адресу (tus: HEAD + PATCH), поэтому обрыв сети продолжается с места обрыва, а не с нуля.
 *
 * Докачка после перезагрузки страницы: файл и адрес из памяти пропали, но принятые Stream байты
 * остались. Карточка передаёт адрес незавершённой загрузки того же файла (`resumeUrl`), и байты
 * идут по нему с места обрыва без нового токена; мёртвый адрес (404/410) заменяется новым токеном.
 *
 * Запись шага может смениться (отмена и новый файл), поэтому колбэки tus и таймеры держат
 * свою запись и действуют, только пока она всё ещё числится за шагом (`current`).
 */
class VideoUploadsStore {
  private entries = new Map<number, UploadEntry>();
  private listeners = new Set<() => void>();
  private snapshot: StepVideoUpload[] = [];
  private guardsBound = false;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = () => this.snapshot;

  start = (
    stepId: number,
    file: File,
    subject: StepVideoUploadSubject,
    handlers: StepVideoUploadHandlers,
    options: StepVideoUploadStartOptions = {},
  ) => {
    // Новый адрес сам ставит заготовку на место прежней, отдельно удалять её не нужно
    this.drop(stepId);
    const resumeUrl = options.resumeUrl ?? null;
    this.entries.set(stepId, {
      state: {
        stepId,
        lessonTitle: subject.lessonTitle?.trim() || null,
        stepNumber: subject.stepNumber,
        fileName: file.name,
        fileSize: file.size,
        status: 'queued',
        progress: 0,
        error: null,
        canResume: false,
        resumed: resumeUrl !== null,
      },
      file,
      handlers,
      upload: null,
      hasUploadUrl: false,
      resumeUrl,
      resuming: false,
      stallTimer: null,
      stallResumes: 0,
    });
    this.commit();
    this.pump();
  };

  /** Отмена: загрузка прерывается, заготовка шага убирается, у шага снова нет видео */
  cancel = (stepId: number) => {
    const entry = this.drop(stepId);
    if (!entry) return;
    // Пока адрес только запрашивался, заготовку уберёт продолжение begin()
    if (entry.hasUploadUrl) {
      void entry.handlers.removeVideo().catch(() => undefined);
    }
    this.pump();
  };

  retry = (stepId: number) => {
    const entry = this.entries.get(stepId);
    if (!entry || entry.state.status !== 'error') return;
    if (entry.upload && entry.state.canResume) {
      this.resume(entry);
      return;
    }
    entry.upload = null;
    entry.hasUploadUrl = false;
    entry.resumeUrl = null;
    entry.resuming = false;
    entry.stallResumes = 0;
    this.setState(entry, {
      status: 'queued',
      progress: 0,
      error: null,
      canResume: false,
      resumed: false,
    });
    this.pump();
  };

  /** Запись убирается без действий на сервере: состояние шага уже известно из деталей урока */
  forget = (stepId: number) => {
    if (this.drop(stepId)) this.pump();
  };

  /** Запись шага, если за шагом всё ещё числится именно она */
  private current(expected: UploadEntry): UploadEntry | null {
    const entry = this.entries.get(expected.state.stepId);
    return entry === expected ? entry : null;
  }

  private drop(stepId: number): UploadEntry | null {
    const entry = this.entries.get(stepId);
    if (!entry) return null;
    this.clearStall(entry);
    if (entry.upload) {
      void entry.upload.abort();
      entry.upload = null;
    }
    this.entries.delete(stepId);
    this.commit();
    return entry;
  }

  private setState(entry: UploadEntry, patch: Partial<StepVideoUpload>) {
    const current = this.current(entry);
    if (!current) return;
    current.state = { ...current.state, ...patch };
    this.commit();
  }

  private commit() {
    this.snapshot = Array.from(this.entries.values(), (entry) => entry.state);
    this.syncGuards();
    this.listeners.forEach((listener) => listener());
  }

  // Пока идёт загрузка: предупреждение при закрытии вкладки и слежение за сетью
  private syncGuards() {
    const hasActive = this.snapshot.some(isActiveUpload);
    if (hasActive && !this.guardsBound) {
      window.addEventListener('beforeunload', preventUnload);
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
      this.guardsBound = true;
    } else if (!hasActive && this.guardsBound) {
      window.removeEventListener('beforeunload', preventUnload);
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
      this.guardsBound = false;
    }
  }

  private pump() {
    let active = this.snapshot.filter(
      (upload) => upload.status !== 'queued' && isActiveUpload(upload),
    ).length;
    for (const entry of this.entries.values()) {
      if (active >= MAX_PARALLEL_UPLOADS) break;
      if (entry.state.status === 'queued') {
        active += 1;
        void this.begin(entry);
      }
    }
  }

  private async begin(entry: UploadEntry) {
    const started = this.current(entry);
    if (!started) return;
    // Докачка: адрес незавершённой загрузки уже известен, токен не нужен
    const { resumeUrl } = started;
    started.resumeUrl = null;
    let uploadUrl = resumeUrl;
    if (!uploadUrl) {
      this.setState(started, { status: 'preparing', progress: 0, error: null });
      try {
        uploadUrl = await started.handlers.getUploadUrl(started.file);
      } catch (error) {
        if (!this.current(started)) return;
        this.fail(
          started,
          getErrorMessage(error, 'Failed to prepare upload. Please try again.'),
          false,
        );
        return;
      }
    }
    const current = this.current(started);
    if (!current) {
      // Отменили, пока ждали адрес: заготовка на сервере уже создана
      void started.handlers.removeVideo().catch(() => undefined);
      return;
    }

    current.hasUploadUrl = true;
    current.resuming = resumeUrl !== null;
    current.upload = new TusUpload(current.file, {
      uploadUrl,
      chunkSize: CHUNK_SIZE,
      retryDelays: RETRY_DELAYS,
      uploadSize: current.file.size,
      // Адрес выдаётся заново на каждую загрузку, в localStorage его не храним
      storeFingerprintForResuming: false,
      onError: (error) => this.handleError(current, error),
      onProgress: (sent, total) => this.handleProgress(current, sent, total),
      onSuccess: () => this.handleSuccess(current),
    });
    this.setState(current, { status: 'uploading' });
    this.watchStall(current);
    current.upload.start();
  }

  // start() на том же адресе: tus спрашивает HEAD, с какого байта продолжать
  private resume(entry: UploadEntry) {
    const current = this.current(entry);
    if (!current?.upload) return;
    this.setState(current, { status: 'uploading', error: null });
    this.watchStall(current);
    current.upload.start();
  }

  private fail(entry: UploadEntry, message: string, canResume: boolean) {
    this.clearStall(entry);
    this.setState(entry, { status: 'error', error: message, canResume });
    this.pump();
  }

  private handleError(entry: UploadEntry, error: Error | DetailedError) {
    const current = this.current(entry);
    if (!current) return;
    this.clearStall(current);
    if (isOffline()) {
      this.setState(current, { status: 'paused' });
      return;
    }
    const status = getResponseStatus(error);
    if (status === 404 || status === 410) {
      // Адреса больше нет: срок истёк или видео шага заменили из другой вкладки. Заготовка
      // теперь чужая или удалена, поэтому «убрать видео» её не трогает
      current.upload = null;
      current.hasUploadUrl = false;
      if (current.resuming) {
        // Файл только что выбран заново, а докачать по старому адресу нельзя: грузим с новым токеном
        current.resuming = false;
        this.setState(current, {
          status: 'queued',
          progress: 0,
          error: null,
          canResume: false,
          resumed: false,
        });
        this.pump();
        return;
      }
      this.fail(
        current,
        'The upload link has expired or the video was replaced. Upload the file again.',
        false,
      );
      return;
    }
    this.fail(current, `Upload error: ${error.message}`, true);
  }

  private handleProgress(entry: UploadEntry, sent: number, total: number | null) {
    const current = this.current(entry);
    if (!current) return;
    // Байты пошли по адресу докачки: он живой
    current.resuming = false;
    this.watchStall(current);
    this.setState(current, {
      status: 'uploading',
      progress: total && total > 0 ? Math.min(100, Math.round((sent / total) * 100)) : 0,
    });
  }

  private handleSuccess(entry: UploadEntry) {
    const current = this.current(entry);
    if (!current) return;
    this.clearStall(current);
    current.upload = null;
    current.resuming = false;
    this.setState(current, { status: 'uploaded', progress: 100, error: null });
    current.handlers.onUploaded?.();
    setTimeout(() => {
      if (this.current(current)?.state.status === 'uploaded') current.handlers.onUploaded?.();
    }, UPLOADED_REFRESH_MS);
    setTimeout(() => {
      const kept = this.current(current);
      if (kept && kept.state.status === 'uploaded') this.drop(kept.state.stepId);
    }, UPLOADED_KEEP_MS);
    this.pump();
  }

  private watchStall(entry: UploadEntry) {
    const current = this.current(entry);
    if (!current) return;
    this.clearStall(current);
    current.stallTimer = setTimeout(() => {
      const stalled = this.current(current);
      if (!stalled?.upload) return;
      stalled.stallTimer = null;
      void stalled.upload.abort();
      if (stalled.stallResumes < STALL_RESUME_LIMIT) {
        stalled.stallResumes += 1;
        // Не сразу: прерванный запрос должен завершиться до нового start()
        setTimeout(() => this.resume(stalled), 0);
        return;
      }
      this.fail(stalled, 'Upload stalled. Check your connection and try again.', true);
    }, STALL_TIMEOUT_MS);
  }

  private clearStall(entry: UploadEntry) {
    const current = this.current(entry);
    if (!current?.stallTimer) return;
    clearTimeout(current.stallTimer);
    current.stallTimer = null;
  }

  private handleOnline = () => {
    for (const entry of this.entries.values()) {
      if (entry.state.status === 'paused') this.resume(entry);
    }
  };

  private handleOffline = () => {
    for (const entry of this.entries.values()) {
      if (entry.state.status === 'uploading' && entry.upload) {
        this.clearStall(entry);
        void entry.upload.abort();
        this.setState(entry, { status: 'paused' });
      }
    }
  };
}

export const videoUploads = new VideoUploadsStore();

export const useVideoUploads = () =>
  useSyncExternalStore(videoUploads.subscribe, videoUploads.getSnapshot);

// Строка сравнивается по значению: прогресс загрузок её не меняет
const getUploadStepIdsKey = () =>
  videoUploads
    .getSnapshot()
    .map((upload) => upload.stepId)
    .join(',');

/**
 * Шаги, у которых есть загрузка в менеджере. Перерисовывает только при появлении и уходе
 * загрузок, а не на каждом шаге прогресса — для страниц, которым прогресс не нужен
 */
export const useVideoUploadStepIds = () => {
  const key = useSyncExternalStore(videoUploads.subscribe, getUploadStepIdsKey);
  return useMemo(() => (key ? key.split(',').map(Number) : []), [key]);
};

/** Загрузка видео шага, если она идёт или закончилась ошибкой; stepId нет у несохранённого шага */
export const useStepVideoUpload = (stepId?: number) => {
  const uploads = useVideoUploads();
  return stepId ? (uploads.find((upload) => upload.stepId === stepId) ?? null) : null;
};

export const useStepVideoUploadsFor = (stepIds: Array<number | undefined>) => {
  const uploads = useVideoUploads();
  return uploads.filter((upload) => stepIds.includes(upload.stepId));
};
